(() => {
  const SOURCE = 'sportszone-flyx-inventory';
  const PACKAGE_PROP = '_SportsZone FLY-X package';
  const PARENT_KEY_PROP = '_SportsZone FLY-X parent key';
  const COMPONENT_PROP = '_SportsZone FLY-X component';
  const ELIGIBLE_SKATE_IDS = new Set([
    15242199433580,
    15242199466348,
    15242199499116,
    15242199531884,
    15242360258924,
    15242360652140,
    15242360684908,
    15242360717676,
    15245454475628,
    15242326507884,
    15242326704492,
    15242326475116,
    15242326671724,
  ]);

  const originalFetch = window.fetch.bind(window);
  let remoteRunnerVariantsPromise = null;
  let syncPromise = null;
  let syncQueued = false;

  const rootUrl = () => window.Shopify?.routes?.root || '/';

  const routePath = (input) => {
    const value = typeof input === 'string' || input instanceof URL ? String(input) : String(input?.url || '');
    try {
      return new URL(value, window.location.origin).pathname;
    } catch (_error) {
      return value.split('?')[0];
    }
  };

  const isCartAdd = (input) => /\/cart\/add(?:\.js)?\/?$/.test(routePath(input));
  const isCartMutation = (input) => /\/cart\/(?:add|change|update|clear)(?:\.js)?\/?$/.test(routePath(input));

  const numericId = (value) => {
    const match = String(value || '').match(/(\d+)$/);
    return match ? Number(match[1]) : 0;
  };

  const parseJsonText = (text, fallback = null) => {
    try {
      return JSON.parse(text);
    } catch (_error) {
      return fallback;
    }
  };

  const parseBody = (body) => {
    if (typeof body !== 'string') return null;
    return parseJsonText(body, null);
  };

  const cloneOptionsWithBody = (options, payload) => ({
    ...(options || {}),
    body: JSON.stringify(payload),
  });

  const propertiesOf = (line) => line?.properties && typeof line.properties === 'object' ? line.properties : {};

  const isRegularFlyXProperties = (properties) =>
    properties?.['Steel choice'] === 'BAUER FLY-X' &&
    properties?.['Steel package'] === 'Included with regular skate package';

  const isEligiblePayloadSkate = (item) => isRegularFlyXProperties(propertiesOf(item));

  const isEligibleCartSkate = (item) =>
    ELIGIBLE_SKATE_IDS.has(Number(item?.product_id || 0)) &&
    isRegularFlyXProperties(propertiesOf(item));

  const holderFromRunnerSize = (value) => {
    const text = String(value || '').trim();
    const first = text.split('/')[0] || text;
    const holder = Number.parseInt(first.replace(/[^\d]/g, ''), 10);
    return Number.isFinite(holder) && holder > 0 ? holder : 0;
  };

  const variantHolder = (variant) => {
    const value = variant?.title || variant?.option1 || variant?.name || '';
    const holder = Number.parseInt(String(value).replace(/[^\d]/g, ''), 10);
    return Number.isFinite(holder) ? holder : 0;
  };

  const normalizeRunnerVariant = (variant) => ({
    ...variant,
    id: numericId(variant?.id),
    available: variant?.available !== false,
  });

  const variantsFromDom = () => {
    const nodes = document.querySelectorAll('[data-sz-fly-x-variants]');
    for (const node of nodes) {
      const variants = parseJsonText(node.textContent || '[]', []);
      if (Array.isArray(variants) && variants.length) return variants.map(normalizeRunnerVariant);
    }
    return [];
  };

  const fetchRunnerVariants = async () => {
    const fromDom = variantsFromDom();
    if (fromDom.length) return fromDom;

    if (!remoteRunnerVariantsPromise) {
      remoteRunnerVariantsPromise = originalFetch(
        `${rootUrl()}products/bauer-fly-x-hockey-skate-runners.js`,
        {
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        }
      )
        .then(async (response) => {
          if (!response.ok) throw new Error('Could not load BAUER FLY-X runner variants.');
          const product = await response.json();
          return Array.isArray(product?.variants) ? product.variants.map(normalizeRunnerVariant) : [];
        })
        .catch((error) => {
          remoteRunnerVariantsPromise = null;
          throw error;
        });
    }

    return remoteRunnerVariantsPromise;
  };

  const findRunnerVariant = async (holder) => {
    if (!holder) return null;
    const variants = await fetchRunnerVariants();
    return variants.find((variant) => variantHolder(variant) === holder) || null;
  };

  const packageKey = (mainItem, runnerId) => {
    const existing = propertiesOf(mainItem)[PACKAGE_PROP];
    if (existing) return String(existing);
    const random = Math.random().toString(36).slice(2, 8);
    return `flyx-${numericId(mainItem?.id)}-${runnerId}-${Date.now()}-${random}`;
  };

  const parentTitleFromPage = () =>
    document.querySelector('[data-sz-steel-choice]')?.dataset?.parentTitle || 'Configured BAUER skate';

  const injectMatchedRunners = async (payload) => {
    if (!Array.isArray(payload?.items) || !payload.items.length) return { payload, changed: false };

    const originalItems = [...payload.items];
    let changed = false;

    for (const mainItem of originalItems) {
      if (!isEligiblePayloadSkate(mainItem)) continue;

      const properties = { ...propertiesOf(mainItem) };
      const holder = holderFromRunnerSize(properties['Runner size']);
      const runner = await findRunnerVariant(holder);

      if (!holder || !runner?.id || runner.available === false) {
        throw new Error('The matched BAUER FLY-X runner is no longer available for this skate size. Please choose another steel option or skate size.');
      }

      const key = packageKey(mainItem, runner.id);
      properties[PACKAGE_PROP] = key;
      mainItem.properties = properties;

      const alreadyIncluded = payload.items.some((item) => {
        const itemProperties = propertiesOf(item);
        return numericId(item?.id) === runner.id && itemProperties[PACKAGE_PROP] === key;
      });

      if (!alreadyIncluded) {
        payload.items.push({
          id: runner.id,
          quantity: Math.max(1, Number(mainItem.quantity || 1)),
          properties: {
            'Runner for': parentTitleFromPage(),
            'Matched runner size': properties['Runner size'] || String(runner.title || '').trim(),
            'Steel package': 'FLY-X package runner — price adjusted with skate',
            [PACKAGE_PROP]: key,
            [COMPONENT_PROP]: 'BAUER FLY-X runner',
          },
        });
      }

      changed = true;
    }

    return { payload, changed };
  };

  const fetchCart = async () => {
    const response = await originalFetch(`${rootUrl()}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) throw new Error('Could not read cart.');
    return response.json();
  };

  const cartDrawerPayload = () => {
    const cartDrawer = document.querySelector('cart-drawer');
    const payload = {};
    if (cartDrawer?.getSectionsToRender) {
      payload.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
      payload.sections_url = window.location.pathname;
    }
    return { cartDrawer, payload };
  };

  const addRunnerForCartLine = async (parent, runner, packageId = '', parentKey = '') => {
    const { cartDrawer, payload } = cartDrawerPayload();
    payload.items = [{
      id: runner.id,
      quantity: Math.max(1, Number(parent.quantity || 1)),
      properties: {
        'Runner for': `${parent.product_title || 'Configured BAUER skate'}${parent.variant_title ? ` — ${parent.variant_title}` : ''}`,
        'Matched runner size': propertiesOf(parent)['Runner size'] || String(runner.title || '').trim(),
        'Steel package': 'FLY-X package runner — price adjusted with skate',
        ...(packageId ? { [PACKAGE_PROP]: packageId } : { [PARENT_KEY_PROP]: parentKey || parent.key }),
        [COMPONENT_PROP]: 'BAUER FLY-X runner',
      },
    }];

    const response = await originalFetch(`${rootUrl()}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok || data?.status) throw new Error(data?.description || data?.message || 'Could not add the matched FLY-X runner.');
    return { data, cartDrawer };
  };

  const changeCartLine = async (key, quantity) => {
    const response = await originalFetch(`${rootUrl()}cart/change.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ id: key, quantity }),
    });
    const cart = await response.json();
    if (!response.ok || cart?.status) throw new Error(cart?.description || cart?.message || 'Could not synchronize FLY-X runner quantity.');
    return cart;
  };

  const notifySavings = () => {
    window.setTimeout(() => window.SportsZoneFlyXSavings?.syncDiscount?.(), 20);
  };

  const syncPackage = async () => {
    if (syncPromise) return syncPromise;

    syncPromise = (async () => {
      let cart = await fetchCart();
      const parents = (cart.items || []).filter(isEligibleCartSkate);
      const parentKeys = new Set(parents.map((item) => item.key));
      const parentPackageIds = new Set(parents.map((item) => propertiesOf(item)[PACKAGE_PROP]).filter(Boolean));
      const components = (cart.items || []).filter((item) => propertiesOf(item)[COMPONENT_PROP] === 'BAUER FLY-X runner');
      let cartChanged = false;

      for (const component of components) {
        const props = propertiesOf(component);
        const packageId = props[PACKAGE_PROP];
        const parentKey = props[PARENT_KEY_PROP];
        const orphaned = packageId
          ? !parentPackageIds.has(packageId)
          : parentKey
            ? !parentKeys.has(parentKey)
            : true;

        if (orphaned) {
          cart = await changeCartLine(component.key, 0);
          cartChanged = true;
        }
      }

      if (cartChanged) cart = await fetchCart();

      for (const parent of (cart.items || []).filter(isEligibleCartSkate)) {
        const props = propertiesOf(parent);
        const holder = holderFromRunnerSize(props['Runner size']);
        const runner = await findRunnerVariant(holder);
        if (!runner?.id || runner.available === false) {
          console.error('SportsZone FLY-X inventory sync: matched runner unavailable', { holder, parent });
          continue;
        }

        const parentPackageId = props[PACKAGE_PROP];
        const linked = (cart.items || []).find((item) => {
          const itemProps = propertiesOf(item);
          if (itemProps[COMPONENT_PROP] !== 'BAUER FLY-X runner') return false;
          if (parentPackageId && itemProps[PACKAGE_PROP] === parentPackageId) return true;
          return itemProps[PARENT_KEY_PROP] === parent.key;
        });

        if (!linked) {
          await addRunnerForCartLine(parent, runner, parentPackageId, parent.key);
          cart = await fetchCart();
          cartChanged = true;
          continue;
        }

        if (numericId(linked.variant_id || linked.id) !== runner.id) {
          cart = await changeCartLine(linked.key, 0);
          await addRunnerForCartLine(parent, runner, parentPackageId, parent.key);
          cart = await fetchCart();
          cartChanged = true;
          continue;
        }

        if (Number(linked.quantity || 0) !== Number(parent.quantity || 0)) {
          cart = await changeCartLine(linked.key, Number(parent.quantity || 0));
          cart = await fetchCart();
          cartChanged = true;
        }
      }

      if (cartChanged && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: cart });
      }

      notifySavings();
    })()
      .catch((error) => console.error('SportsZone FLY-X inventory sync error', error))
      .finally(() => { syncPromise = null; });

    return syncPromise;
  };

  const scheduleSync = (delay = 20) => {
    if (syncQueued) return;
    syncQueued = true;
    window.setTimeout(() => {
      syncQueued = false;
      syncPackage();
    }, delay);
  };

  window.fetch = async (input, options = {}) => {
    try {
      if (isCartAdd(input)) {
        const payload = parseBody(options?.body);
        if (payload) {
          const result = await injectMatchedRunners(payload);
          if (result.changed) {
            const response = await originalFetch(input, cloneOptionsWithBody(options, result.payload));
            scheduleSync(10);
            return response;
          }
        }
      }
    } catch (error) {
      console.error('SportsZone FLY-X cart guard error', error);
      throw error;
    }

    const response = await originalFetch(input, options);
    if (isCartMutation(input)) scheduleSync(20);
    return response;
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source === SOURCE) return;
      scheduleSync(25);
    });
  }

  window.SportsZoneFlyXInventory = { sync: syncPackage };
  window.setTimeout(() => scheduleSync(0), 450);
})();
